import type { CSSProperties, FC, ReactNode } from 'react'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { FigLabel } from '@/components/generals/fig-label'
import { PageHeader } from '@/components/generals/page-header'
import { PrimaryButton } from '@/components/generals/primary-button'
import { Reveal } from '@/components/generals/reveal'
import { CapabilitySection } from '@/components/platform/capability-section'
import { CapabilityTimeline } from '@/components/platform/capability-timeline'
import { formatNumber } from '@/components/generals/count-up'

import { CAPABILITIES } from '@/constants/platform'
import { STUDIO_FACTS } from '@/constants/studio'

const delay = (value: number): CSSProperties => ({ '--delay': `${value}ms` } as CSSProperties)

const Platform: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo
                title='Platform'
                description='Capture, model, monitor and archive: the four instruments Norrvik uses to keep one live model of every building, from the first survey to the next fifty years.'
                figure='Fig. 01 / Platform / Four instruments'
                shape='archive'
            />

            <PageHeader
                fig='01'
                title='Platform'
                detail='Four instruments'
                note='Capture / Model / Monitor / Archive'
                lines={['One model,', 'from first scan', 'to final decade.']}
                intro={(
                    <p>
                        Every Norrvik building runs on a single live model. It starts as a 2 mm scan of the site, carries the design through construction, and keeps taking readings long after the keys are handed over.
                    </p>
                )}
                readouts={[
                    { label: 'Points captured', value: formatNumber(STUDIO_FACTS.points, 0) },
                    { label: 'Projects', value: STUDIO_FACTS.projects },
                    { label: 'Countries', value: STUDIO_FACTS.countries },
                    { label: 'Archive', value: `${STUDIO_FACTS.archivePb} PB` }
                ]}
            />

            {CAPABILITIES.map((capability, index) => (
                <CapabilitySection key={capability.id} capability={capability} fig={String(index + 2).padStart(2, '0')} />
            ))}

            <section id='survey-to-second-life' aria-labelledby='timeline-title' className='relative'>
                <Reveal className='relative px-gutter pb-20 pt-20 desk:pb-28 desk:pt-28'>
                    <span className='draw-x absolute inset-x-0 top-0 h-px bg-hairline' aria-hidden='true' />

                    <div className='grid gap-8 desk:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]'>
                        <div>
                            <FigLabel fig='06' title='Timeline' detail='A typical first year' note='Month 1 to month 12 / four bands' />

                            <h2 id='timeline-title' className='display-lg mt-10'>
                                <span className='reveal-line'>
                                    <span className='reveal-inner text-ink' style={delay(80)}>
                                        From survey
                                    </span>
                                </span>

                                <span className='reveal-line'>
                                    <span className='reveal-inner text-dim' style={delay(170)}>
                                        to second life.
                                    </span>
                                </span>
                            </h2>
                        </div>

                        <p className='reveal-fade body-copy self-end text-ink/85 desk:justify-self-end' style={delay(260)}>
                            The four instruments overlap rather than hand over. Capture returns for an as-built scan before handover, monitoring starts the day the building opens, and the archive keeps every version from the first point.
                        </p>
                    </div>

                    <div className='mt-16'>
                        <CapabilityTimeline />
                    </div>
                </Reveal>
            </section>

            <section aria-labelledby='platform-next' className='relative'>
                <Reveal className='relative px-gutter pb-24 pt-20 desk:pb-32 desk:pt-28'>
                    <span className='draw-x absolute inset-x-0 top-0 h-px bg-hairline' aria-hidden='true' />

                    <FigLabel fig='07' title='Next step' detail='Engage the studio' note='Replies within two working days' />

                    <div className='mt-10 flex flex-col gap-10 desk:flex-row desk:items-end desk:justify-between'>
                        <h2 id='platform-next' className='display-lg max-w-[18ch]'>
                            <span className='reveal-line'>
                                <span className='reveal-inner text-ink' style={delay(80)}>
                                    Bring us a building,
                                </span>
                            </span>

                            <span className='reveal-line'>
                                <span className='reveal-inner text-dim' style={delay(170)}>
                                    standing or still on paper.
                                </span>
                            </span>
                        </h2>

                        <div className='reveal-fade' style={delay(300)}>
                            <PrimaryButton href='/contact'>
                                Engage the studio
                            </PrimaryButton>
                        </div>
                    </div>
                </Reveal>
            </section>
        </Layout>
    )
}

export default Platform
